import React from "react";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Tooltip, Typography} from "@mui/material";
import {HighlightOff} from "@mui/icons-material";
import isEqual from "lodash.isequal";
import { DialogHeader } from "../../../../../theme";

// region TypeScript Interfaces
interface DifferenceDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    disabled?: boolean;
    value: any;
    compare: any;
    rejectChange: () => void;
}

export interface AttributeDiffProps {
    disabled?: boolean;
    value: any;
    compare: any;
    label: React.ReactNode;
    labelstyles?: React.CSSProperties;
    rejectChange: () => void;
}

export interface AttributeDiffWithInputProps {
    disabled?: boolean;
    value: any;
    compare: any;
    field: React.ReactElement<any>;
    rejectChange: () => void;
}
// endregion

const DifferenceDialog = (props: DifferenceDialogProps) => {
    const {open, setOpen, disabled} = props;

    return (
        <Dialog open={open} onClose={() => setOpen(false)}
                maxWidth={"sm"} fullWidth={true}
                slotProps={{
                    paper: {style: {borderRadius: "12px"}}
                }}
        >
            <DialogTitle>
                <Grid item container justifyContent={"space-between"} alignItems={"center"}>
                    <Grid item container style={{flex: 0.8, flexDirection: "column"}}>
                        <DialogHeader>
                            Product Attribute Differs!
                        </DialogHeader>
                    </Grid>

                    <IconButton onClick={() => setOpen(false)} size="large">
                        <HighlightOff style={{color: "red"}}/>
                    </IconButton>
                </Grid>
            </DialogTitle>
            <DialogContent style={{padding: "0 24px"}}>
                <Grid container>
                    <Typography><b>Current Value:</b> {JSON.stringify(props.value)}</Typography>
                </Grid>

                <Grid container>
                    <Typography><b>My Catalog Value:</b> {JSON.stringify(props.compare)}</Typography>
                </Grid>
            </DialogContent>
            <DialogActions style={{padding: "24px"}}>
                <Button variant="text" disabled={disabled} onClick={() => setOpen(false)}>Cancel</Button>
                <Button variant="text" color="error" disabled={disabled} onClick={() => props.rejectChange()}>Reject Change</Button>
            </DialogActions>
        </Dialog>
    );
}

export const AttributeDiff = React.memo((props: AttributeDiffProps) => {
    const {disabled, value, compare} = props;
    const [open, setOpen] = React.useState(false);

    if (disabled) return <>{props.label}</>;
    if (isEqual(value, compare)) return <>{props.label}</>;

    return (
        <>
            <Tooltip title={"Attribute differs, please review."}>
                <Grid container alignItems={"center"}
                      style={{flex: 1, color: "#ffa726", overflow: "hidden", textAlign: "left", textOverflow: "ellipsis"}}
                      onClick={() => setOpen(true)}
                >
                    <Typography component={'span'}
                                style={{fontSize: "1rem", lineHeight: "inherit", ...props.labelstyles}}>
                        {props.label} (Attribute Differs)
                    </Typography>
                </Grid>
            </Tooltip>
            <DifferenceDialog open={open} setOpen={setOpen} disabled={disabled} value={props.value}
                              compare={props.compare} rejectChange={props.rejectChange}/>
        </>
    )
});

export const AttributeDiffWithInput = React.memo((props: AttributeDiffWithInputProps) => {
    const {disabled, value, compare} = props;
    const [open, setOpen] = React.useState(false);

    if (disabled) return props.field;
    if (isEqual(value, compare)) return props.field;

    const field = React.cloneElement(props.field,
        {
            label: (
                <Tooltip title={"Attribute differs, please review."}>
                    <Grid container alignItems={"center"}
                          style={{flex: 1, color: "#ffa726", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis"}}
                          onClick={() => setOpen(true)}
                    >
                        <Typography style={{fontSize: "1rem", lineHeight: "inherit"}}>
                            {(props.field.props as any).label} (Attribute Differs)
                        </Typography>
                    </Grid>
                </Tooltip>
            )
        } as any
    );
    return (
        <>
            {field}
            <DifferenceDialog open={open} setOpen={setOpen} disabled={disabled} value={props.value}
                              compare={props.compare} rejectChange={props.rejectChange}/>
        </>
    )
})
